import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ConfigService, OrderableItem, OrderedItem, IngredientChoice } from 'ngx-web-api';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IngredientSelection } from '../../../domain/ingredient-selection';
import { Animations } from '../../../animations';
import { Subject } from 'rxjs';
import { UiOrchestratorService } from '../../../core/services/ui-orchestrator.service';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'fts-item-instructions',
  templateUrl: './item-instructions.component.html',
  styleUrls: ['./item-instructions.component.scss'],
  animations: [Animations.expanded],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemInstructionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  orderableItem: OrderableItem;
  @Input()
  item: OrderedItem;
  @Input()
  instructions: string;
  @Input()
  isInModal: boolean;
  @Input()
  inItemEditorModal = false;
  @Output()
  instructionsChange: EventEmitter<string> = new EventEmitter();
  @Output()
  toggleIngredients: EventEmitter<IngredientSelection[]> = new EventEmitter();
  @Output()
  toggleQualifier: EventEmitter<IngredientSelection> = new EventEmitter();
  @Output()
  chooseHalf: EventEmitter<IngredientSelection> = new EventEmitter();

  customerInstructionsDisclaimer: string;
  allowCustomerInstructions: boolean;
  maxSpecialInstructionsLength: number;
  expanded: boolean;
  faPlus = faPlus;
  faMinus = faMinus;

  instructionChoice: IngredientChoice;
  allowCustomerNotes: boolean;

  private readonly debounceTime = 1000;
  private instructionsText: Subject<string> = new Subject();
  private destroy$: Subject<void> = new Subject<void>();

  constructor(private configService: ConfigService, private uiOrchestrationService: UiOrchestratorService) {}

  ngOnInit() {
    this.configService.storeConfig$.subscribe(storeConfig => {
      this.expanded = storeConfig.itemInstructionsExpanded;
      this.customerInstructionsDisclaimer = storeConfig.customerInstructionsDisclaimer;
      this.allowCustomerInstructions = storeConfig.allowCustomerInstructions;
      this.calcAllowCustomerNotes();
      this.maxSpecialInstructionsLength = storeConfig.maxSpecialInstructionsLength;
    });

    this.instructionsText
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((instructions: string) => this.instructionsChange.emit(instructions));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!!changes['orderableItem'] && !!changes['orderableItem'].currentValue) {
      this.instructionChoice = this.orderableItem.choices.find(c => c.isInstructions);
      this.calcAllowCustomerNotes();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setInstructions(instructions: string) {
    this.instructionsText.next(instructions);
  }

  calcAllowCustomerNotes() {
    this.allowCustomerNotes = this.allowCustomerInstructions || (this.orderableItem && this.orderableItem.allowWebCustomerInstructions);
  }

  toggleInstructions(selections: IngredientSelection[]) {
    this.toggleIngredients.emit(selections);
  }

  toggleExpand() {
    this.expanded = !this.expanded;
  }

  expandedAnimationEnd() {
    this.uiOrchestrationService.recalculateStepControlsPosition();
  }

  onToggleQualifier(selection: IngredientSelection) {
    this.toggleQualifier.emit(selection);
  }

  onChooseHalf(selection: IngredientSelection) {
    this.chooseHalf.emit(selection);
  }
}
